import { Platform } from 'react-native';

// Conditionally import NetInfo only for native platforms
let NetInfo: any = null;
if (Platform.OS !== 'web') {
  NetInfo = require('@react-native-community/netinfo').default;
}

/**
 * Network state service that works on both native and web
 * Provides unified network connectivity detection
 */
// Debounce delay to prevent rapid state flickering (WiFi↔cellular transitions)
const DEBOUNCE_MS = 300;

class NetworkService {
  private isWeb = Platform.OS === 'web';
  private listeners: ((isConnected: boolean) => void)[] = [];
  private currentState: boolean = true; // Assume connected initially
  private offlineSinceTime: number | null = null;
  private initialized: boolean = false; // Guard against duplicate initialization
  private initInFlight: Promise<void> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingState: boolean | null = null; // Track pending state during debounce

  async initialize(): Promise<void> {
    // Prevent duplicate initialization which would register multiple NetInfo listeners
    // and cause race conditions/crashes when network state changes
    if (this.initialized) {
      return;
    }
    if (this.initInFlight) {
      return this.initInFlight;
    }

    this.initInFlight = this.attachListeners();
    try {
      await this.initInFlight;
    } finally {
      this.initInFlight = null;
    }
  }

  private async attachListeners(): Promise<void> {
    try {
      if (this.isWeb) {
        // Web network detection
        this.currentState =
          typeof navigator !== 'undefined' ? navigator.onLine : true;

        if (typeof window !== 'undefined') {
          window.addEventListener('online', this.handleOnline);
          window.addEventListener('offline', this.handleOffline);
        }
      } else {
        // Native network detection
        if (NetInfo) {
          const netInfoState = await NetInfo.fetch();
          this.currentState = netInfoState.isConnected ?? false;

          NetInfo.addEventListener((state: any) => {
            const isConnected = state.isConnected ?? false;
            this.updateConnectionState(isConnected);
          });
        } else {
          console.warn('NetInfo not available, assuming online');
        }
      }
      this.initialized = true;
    } catch (error) {
      this.initialized = false;
      throw error;
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private handleOnline = () => {
    this.updateConnectionState(true);
  };

  private handleOffline = () => {
    this.updateConnectionState(false);
  };

  private updateConnectionState(isConnected: boolean) {
    if (this.currentState !== isConnected) {
      // Store the pending state
      this.pendingState = isConnected;

      // Clear any existing debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Debounce rapid state changes (prevents WiFi↔cellular flickering from triggering multiple syncs)
      this.debounceTimer = setTimeout(() => {
        // Only proceed if state is still different (wasn't reverted during debounce)
        if (
          this.pendingState !== null &&
          this.currentState !== this.pendingState
        ) {
          this.currentState = this.pendingState;

          // Track offline time
          if (!this.currentState) {
            // Going offline - record the time
            this.offlineSinceTime = Date.now();
          } else {
            // Coming back online - clear the offline time
            this.offlineSinceTime = null;
          }

          // Notify all listeners with error isolation
          this.listeners.forEach(listener => {
            try {
              listener(this.currentState);
            } catch (error) {
              console.error('Error in network listener callback:', error);
            }
          });
        }

        this.pendingState = null;
        this.debounceTimer = null;
      }, DEBOUNCE_MS);
    }
  }

  isConnected(): boolean {
    return this.currentState;
  }

  getOfflineSinceTime(): number | null {
    return this.offlineSinceTime;
  }

  addListener(listener: (isConnected: boolean) => void): () => void {
    this.listeners.push(listener);

    // Return cleanup function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  cleanup(): void {
    if (this.isWeb) {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
      }
    }
    // Clear any pending debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingState = null;
    this.listeners = [];
    this.initialized = false; // Allow re-initialization after cleanup
    this.initInFlight = null;
  }
}

export const networkService = new NetworkService();
